/**
 * Build My PC
 * Vanilla JS — no external dependencies
 * Namespace: BPC
 */

(function () {
  'use strict';

  /* ============================================================
     CONFIG — injected by Liquid
     ============================================================ */
  const CFG = window.BPC_CONFIG || {};
  const STEPS = CFG.steps || [];
  const CURRENCY = CFG.currency || '₹';
  const STORAGE_KEY = 'pc_build';
  const PAGE_SIZE = 24;
  const STOREFRONT_TOKEN = CFG.storefrontToken || '';
  const SHOP_DOMAIN = CFG.shop || window.location.hostname;
  const STOREFRONT_URL = `https://${SHOP_DOMAIN}/api/2024-10/graphql.json`;

  /* ============================================================
     STEP FILTER SCHEMA
     Defines which filters exist per step and their display config.
     'values' is intentionally absent — populated dynamically from
     your actual product metafields after fetch.
     ============================================================ */
  const STEP_FILTERS = {
    cpu: {
      tiles: { key: 'brand', label: 'Brand' },
      checkboxes: [
        { key: 'socket_type', label: 'Socket' },
        { key: 'series', label: 'Series' }
      ],
      price: true
    },
    motherboard: {
      checkboxes: [
        { key: 'brand', label: 'Brand' },
        { key: 'ram_type', label: 'RAM Type' },
        { key: 'form_factor', label: 'Form Factor' }
      ],
      price: true
    },
    ram: {
      checkboxes: [
        { key: 'brand', label: 'Brand' },
        { key: 'capacity_gb', label: 'Capacity', suffix: 'GB', numeric: true },
        { key: 'speed_mhz', label: 'Speed', suffix: 'MHz', numeric: true },
        { key: 'kit_type', label: 'Kit' }
      ],
      price: true
    },
    primary_storage: {
      tiles: { key: 'interface', label: 'Drive Type' },
      checkboxes: [
        { key: 'interface', label: 'Drive Type' },
        { key: 'brand', label: 'Brand' },
        { key: 'capacity_gb', label: 'Capacity', suffix: 'GB', numeric: true }
      ],
      price: true
    },
    secondary_storage: {
      tiles: { key: 'interface', label: 'Drive Type' },
      checkboxes: [
        { key: 'interface', label: 'Drive Type' },
        { key: 'brand', label: 'Brand' },
        { key: 'capacity_gb', label: 'Capacity', suffix: 'GB', numeric: true }
      ],
      price: true
    },
    storage: {
      tiles: { key: 'interface', label: 'Drive Type' },
      checkboxes: [
        { key: 'brand', label: 'Brand' },
        { key: 'capacity_gb', label: 'Capacity', suffix: 'GB', numeric: true }
      ],
      price: true
    },
    additional_storage: {
      tiles: { key: 'interface', label: 'Drive Type' },
      checkboxes: [
        { key: 'brand', label: 'Brand' },
        { key: 'capacity_gb', label: 'Capacity', suffix: 'GB', numeric: true }
      ],
      price: true
    },
    ssd: {
      tiles: { key: 'interface', label: 'Drive Type' },
      checkboxes: [
        { key: 'brand', label: 'Brand' },
        { key: 'capacity_gb', label: 'Capacity', suffix: 'GB', numeric: true }
      ],
      price: true
    },
    gpu: {
      tiles: { key: 'brand', label: 'Brand' },
      checkboxes: [
        { key: 'series', label: 'Series' },
        { key: 'vram_gb', label: 'VRAM', suffix: 'GB', numeric: true }
      ],
      price: true
    },
    smps: {
      checkboxes: [
        { key: 'brand', label: 'Brand' },
        { key: 'wattage', label: 'Wattage', suffix: 'W', numeric: true },
        { key: 'psu_rating', label: 'Rating' },
        { key: 'modular_type', label: 'Modular' }
      ],
      price: true
    },
    cooler: {
      tiles: { key: 'cooler_type', label: 'Type' },
      checkboxes: [
        { key: 'brand', label: 'Brand' },
        { key: 'aio_size_mm', label: 'AIO Size', suffix: 'mm', numeric: true, conditional: 'cooler_type:AIO Liquid' }
      ],
      price: true
    },
    cabinet: {
      checkboxes: [
        { key: 'brand', label: 'Brand' },
        { key: 'supported_form_factors', label: 'Form Factor' },
        { key: 'radiator_support', label: 'Radiator Support', suffix: 'mm', numeric: true },
        { key: 'side_panel', label: 'Side Panel' }
      ],
      price: true
    }
  };

  /* ============================================================
     EXTRACT UNIQUE FILTER VALUES FROM FETCHED PRODUCTS
     Called once after each collection is fetched.
     Scans all products and collects unique non-empty metafield
     values for every filter key in that step's schema.
     ============================================================ */
  function buildDynamicFilterValues(stepKey, products) {
    const schema = STEP_FILTERS[stepKey];
    if (!schema) return;

    const allKeys = [];
    if (schema.tiles) allKeys.push(schema.tiles.key);
    (schema.checkboxes || []).forEach(cb => allKeys.push(cb.key));

    allKeys.forEach(key => {
      const seen = new Set();
      products.forEach(p => {
        const raw = getMeta(p, key);
        if (!raw) return;
        // comma-separated fields (socket_compatibility, supported_form_factors)
        if (raw.includes(',')) {
          raw.split(',').map(s => s.trim()).filter(Boolean).forEach(v => seen.add(v));
        } else {
          seen.add(raw.trim());
        }
      });

      // Sort: numeric keys numerically, text keys alphabetically
      const cb = (schema.checkboxes || []).find(c => c.key === key);
      const isNumeric = cb && cb.numeric;
      const sorted = isNumeric
        ? [...seen].sort((a, b) => parseFloat(a) - parseFloat(b))
        : [...seen].sort();

      // Store on schema so renderFilters / renderTileFilters can read it
      if (schema.tiles && schema.tiles.key === key) {
        schema.tiles.values = sorted;
      }
      const cbDef = (schema.checkboxes || []).find(c => c.key === key);
      if (cbDef) cbDef.values = sorted;
    });
  }

  /* ============================================================
     STATE
     ============================================================ */
  let state = {
    currentStep: 0,
    selections: {},  // { cpu: {product}, motherboard: {product}, ... }
    ramQty: 1,       // number of RAM modules selected (capped at motherboard RAM slots)
    filters: {},     // { cpu: { brand: 'Intel', price_min: 0, price_max: 99999, ... }, ... }
    pages: {},       // { cpu: 1, ... }
    sort: 'featured', // featured | price-asc | price-desc
    allProducts: {}, // { cpu: [...], ... } — full fetched pool per step
    fetched: {}      // { cpu: true, ... }
  };

  /* ============================================================
     DOM REFS
     ============================================================ */
  const $ = (id) => document.getElementById(id);
  const els = {
    progressFill: $('bpcProgressFill'),
    progressText: $('bpcProgressText'),
    stepsTabs: $('bpcStepsTabs'),
    grid: $('bpcGrid'),
    empty: $('bpcEmpty'),
    error: $('bpcError'),
    loadMoreWrap: $('bpcLoadMoreWrap'),
    loadMore: $('bpcLoadMore'),
    retry: $('bpcRetry'),
    resetFilters: $('bpcResetFilters'),
    filters: $('bpcFilters'),
    filtersBody: $('bpcFiltersBody'),
    filtersOverlay: $('bpcFiltersOverlay'),
    filterToggle: $('bpcFilterToggle'),
    filtersClose: $('bpcFiltersClose'),
    clearFilters: $('bpcClearFilters'),
    applyFilters: $('bpcApplyFilters'),
    activeFilters: $('bpcActiveFilters'),
    resultCount: $('bpcResultCount'),
    sortBy: $('bpcSortBy'),
    tileFilters: $('bpcTileFilters'),
    summaryBody: $('bpcSummaryBody'),
    summaryFooter: $('bpcSummaryFooter'),
    summaryWattage: $('bpcSummaryWattage'),
    wattageText: $('bpcWattageText'),
    totalPrice: $('bpcTotalPrice'),
    addToCart: $('bpcAddToCart'),
    summaryToggle: $('bpcSummaryToggle'),
    summary: document.querySelector('.bpc-summary'),
    prevStep: $('bpcPrevStep'),
    nextStep: $('bpcNextStep'),
    mobileBar: $('bpcMobileBar'),
    mobileTotal: $('bpcMobileTotal'),
    mobileWattage: $('bpcMobileWattage'),
    mobileViewBuild: $('bpcMobileViewBuild'),
    mobileAddToCart: $('bpcMobileAddToCart')
  };

  /* ============================================================
     INIT
     ============================================================ */
  function init() {
    loadFromStorage();
    bindEvents();
    if (els.sortBy) els.sortBy.value = state.sort;
    renderStepTabs();
    renderSummary();
    goToStep(state.currentStep, true);
  }

  /* ============================================================
     STORAGE
     ============================================================ */
  function loadFromStorage() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        state.selections = parsed.selections || {};
        state.ramQty = parseInt(parsed.ramQty) || 1;
        // Migrate legacy keys if present
        if (state.selections.ssd && !state.selections.primary_storage) {
          state.selections.primary_storage = state.selections.ssd;
          delete state.selections.ssd;
        }
        if (state.selections.storage && !state.selections.primary_storage) {
          state.selections.primary_storage = state.selections.storage;
          delete state.selections.storage;
        }
        if (state.selections.additional_storage && !state.selections.secondary_storage) {
          state.selections.secondary_storage = state.selections.additional_storage;
          delete state.selections.additional_storage;
        }
        state.currentStep = parsed.currentStep || 0;
        state.filters = parsed.filters || {};
        state.sort = parsed.sort || 'featured';
      }
    } catch (e) { /* ignore */ }
  }

  function saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        selections: state.selections,
        ramQty: state.ramQty || 1,
        currentStep: state.currentStep,
        filters: state.filters,
        sort: state.sort
      }));
    } catch (e) { /* ignore */ }
  }

  /* ============================================================
     STEP NAVIGATION
     ============================================================ */
  function goToStep(index, force) {
    if (index < 0 || index >= STEPS.length) return;
    state.currentStep = index;
    updateProgressBar();
    renderStepTabs();
    updateNavButtons();
    fetchAndRenderStep(index);
    saveToStorage();
    // Scroll step tab into view
    const tabEl = els.stepsTabs.querySelector(`[data-step="${index}"]`);
    if (tabEl) tabEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  function updateProgressBar() {
    const pct = ((state.currentStep + 1) / STEPS.length) * 100;
    els.progressFill.style.width = pct + '%';
    els.progressText.textContent = `Step ${state.currentStep + 1} of ${STEPS.length}`;
  }

  function renderStepTabs() {
    els.stepsTabs.querySelectorAll('.bpc-step-tab').forEach((tab, i) => {
      tab.classList.remove('active', 'completed', 'locked');
      const stepKey = STEPS[i].key;
      if (i === state.currentStep) {
        tab.classList.add('active');
      } else if (state.selections[stepKey]) {
        tab.classList.add('completed');
      } else if (!canAccessStep(i)) {
        tab.classList.add('locked');
      }
    });
  }

  function canAccessStep(index) {
    // Can access if all required steps before it are completed
    for (let i = 0; i < index; i++) {
      if (STEPS[i].required && !state.selections[STEPS[i].key]) return false;
    }
    return true;
  }

  function updateNavButtons() {
    els.prevStep.disabled = state.currentStep === 0;
    const isLast = state.currentStep === STEPS.length - 1;
    els.nextStep.textContent = '';
    els.nextStep.innerHTML = isLast
      ? 'Finish'
      : 'Next <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>';
  }

  /* ============================================================
     FETCH PRODUCTS
     ============================================================ */
  async function fetchAndRenderStep(stepIndex) {
    const step = STEPS[stepIndex];
    if (!step) return;

    showSkeletons();

    if (!state.fetched[step.key]) {
      try {
        let collectionsToFetch = [step.collection];
        if (step.key === 'primary_storage' || step.key === 'secondary_storage' || step.key === 'storage' || step.key === 'additional_storage' || step.key === 'ssd') {
          collectionsToFetch = ['internal-storage', 'pc-builder-ssd', 'hdd'];
        }

        let products = [];
        for (const colHandle of collectionsToFetch) {
          try {
            const fetched = await fetchAllProducts(colHandle);
            products.push(...fetched);
          } catch (e) { /* ignore */ }
        }

        // Deduplicate by ID
        const map = new Map();
        products.forEach(p => map.set(p.id, p));
        products = Array.from(map.values());

        const validProducts = products.filter(p => isProductValidForStep(p, step.key));
        state.allProducts[step.key] = validProducts;
        buildDynamicFilterValues(step.key, validProducts);
        state.fetched[step.key] = true;
      } catch (e) {
        showError();
        return;
      }
    } else {
      // Already fetched — rebuild dynamic values in case schema was reset
      buildDynamicFilterValues(step.key, state.allProducts[step.key] || []);
    }

    // Always re-render filters after fetch or re-visit so values are current
    renderFilters(stepIndex);
    renderTileFilters(stepIndex);
    renderActiveFilters(stepIndex);

    state.pages[step.key] = 1;
    renderProducts(stepIndex);
    renderSummary();
  }

  async function fetchAllProducts(handle) {
    const all = [];
    let cursor = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const afterClause = cursor ? `, after: "${cursor}"` : '';
      const query = `{
        collection(handle: "${handle}") {
          products(first: 250${afterClause}) {
            pageInfo { hasNextPage endCursor }
            edges {
              node {
                id
                title
                handle
                description
                vendor
                images(first: 1) {
                  edges { node { url } }
                }
                availableForSale
                variants(first: 1) {
                  edges {
                    node {
                      id
                      availableForSale
                      price { amount }
                    }
                  }
                }
                metafields(
                  identifiers: [
                    {namespace: "pcb", key: "brand"},
                    {namespace: "pcb", key: "socket_type"},
                    {namespace: "pcb", key: "series"},
                    {namespace: "pcb", key: "tdp_watts"},
                    {namespace: "pcb", key: "ram_type"},
                    {namespace: "pcb", key: "ram_slots"},
                    {namespace: "pcb", key: "form_factor"},
                    {namespace: "pcb", key: "nvme_slots"},
                    {namespace: "pcb", key: "sata_ports"},
                    {namespace: "pcb", key: "capacity_gb"},
                    {namespace: "pcb", key: "speed_mhz"},
                    {namespace: "pcb", key: "kit_type"},
                    {namespace: "pcb", key: "interface"},
                    {namespace: "pcb", key: "vram_gb"},
                    {namespace: "pcb", key: "length_mm"},
                    {namespace: "pcb", key: "wattage"},
                    {namespace: "pcb", key: "psu_rating"},
                    {namespace: "pcb", key: "modular_type"},
                    {namespace: "pcb", key: "cooler_type"},
                    {namespace: "pcb", key: "socket_compatibility"},
                    {namespace: "pcb", key: "height_mm"},
                    {namespace: "pcb", key: "aio_size_mm"},
                    {namespace: "pcb", key: "supported_form_factors"},
                    {namespace: "pcb", key: "max_gpu_length_mm"},
                    {namespace: "pcb", key: "max_cooler_height_mm"},
                    {namespace: "pcb", key: "radiator_support"},
                    {namespace: "pcb", key: "max_radiator_mm"},
                    {namespace: "pcb", key: "side_panel"}
                  ]
                ) {
                  namespace
                  key
                  value
                }
              }
            }
          }
        }
      }`;

      const res = await fetch(STOREFRONT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
        },
        body: JSON.stringify({ query })
      });

      if (!res.ok) throw new Error('Storefront API fetch failed');
      const data = await res.json();

      if (data.errors) throw new Error(data.errors[0].message);

      const collection = data.data && data.data.collection;
      if (!collection) break;

      const edges = collection.products.edges || [];
      edges.forEach(({ node }) => {
        // Normalise to same shape the rest of the code expects
        all.push({
          id: parseInt(node.id.replace('gid://shopify/Product/', '')),
          gid: node.id,
          title: node.title,
          handle: node.handle,
          description: node.description || '',
          vendor: node.vendor,
          availableForSale: node.availableForSale !== false,
          images: node.images.edges.map(e => ({ src: e.node.url })),
          variants: node.variants.edges.map(e => ({
            id: parseInt(e.node.id.replace('gid://shopify/ProductVariant/', '')),
            availableForSale: e.node.availableForSale !== false,
            price: e.node.price.amount
          })),
          metafields: node.metafields.filter(Boolean)
        });
      });

      hasNextPage = collection.products.pageInfo.hasNextPage;
      cursor = collection.products.pageInfo.endCursor;
    }

    return all;
  }

  /* ============================================================
     COMPATIBILITY FILTERING (AUTO-INVISIBLE)
     ============================================================ */
  function getCompatibilityFiltered(stepIndex) {
    const step = STEPS[stepIndex];
    let products = state.allProducts[step.key] || [];
    const sel = state.selections;

    if (step.key === 'motherboard' && sel.cpu) {
      const cpuSocket = (getMeta(sel.cpu, 'socket_type') || '').toUpperCase().replace(/\s+/g, '');
      if (cpuSocket) {
        products = products.filter(p => {
          const mbSocket = (getMeta(p, 'socket_type') || '').toUpperCase().replace(/\s+/g, '');
          return mbSocket === cpuSocket;
        });
      }
    }

    if (step.key === 'ram' && sel.motherboard) {
      const mbRamType = (getMeta(sel.motherboard, 'ram_type') || '').toUpperCase().replace(/\s+/g, '');
      if (mbRamType) {
        products = products.filter(p => {
          const ramType = (getMeta(p, 'ram_type') || '').toUpperCase().replace(/\s+/g, '');
          return ramType === mbRamType;
        });
      }
    }

    if (step.key === 'cooler' && sel.cpu) {
      const cpuSocket = (getMeta(sel.cpu, 'socket_type') || '').toUpperCase().replace(/\s+/g, '');
      if (cpuSocket) {
        products = products.filter(p => {
          const compat = getMeta(p, 'socket_compatibility') || '';
          const sockets = compat.split(',').map(s => s.trim().toUpperCase().replace(/\s+/g, '')).filter(Boolean);
          return sockets.includes(cpuSocket);
        });
      }
    }

    if (step.key === 'smps' && sel.gpu) {
      const minWattage = getGpuMinPsuWattage(sel.gpu);
      if (minWattage > 0) {
        products = products.filter(p => {
          const w = parseInt(getMeta(p, 'wattage') || 0);
          return w >= minWattage;
        });
      }
    }

    if (step.key === 'cabinet') {
      if (sel.motherboard) {
        const ff = (getMeta(sel.motherboard, 'form_factor') || '').toUpperCase().replace(/\s+/g, '');
        if (ff) {
          products = products.filter(p => {
            const ffs = (getMeta(p, 'supported_form_factors') || '').toUpperCase().replace(/\s+/g, '');
            const formFactors = ffs.split(',').map(s => s.trim()).filter(Boolean);
            return formFactors.includes(ff);
          });
        }
      }
      if (sel.gpu) {
        const gpuLen = parseInt(getMeta(sel.gpu, 'length_mm') || 0);
        if (gpuLen) products = products.filter(p => parseInt(getMeta(p, 'max_gpu_length_mm') || 9999) >= gpuLen);
      }
      if (sel.cooler) {
        const coolerType = getMeta(sel.cooler, 'cooler_type');
        if (coolerType === 'AIO Liquid') {
          const aioSize = parseInt(getMeta(sel.cooler, 'aio_size_mm') || 0);
          if (aioSize) {
            products = products.filter(p => {
              const rads = (getMeta(p, 'radiator_support') || '120,240,360').split(',').map(s => parseInt(s.trim())).filter(Boolean);
              return rads.includes(aioSize);
            });
          }
        } else {
          const coolerH = parseInt(getMeta(sel.cooler, 'height_mm') || 0);
          if (coolerH) products = products.filter(p => parseInt(getMeta(p, 'max_cooler_height_mm') || 9999) >= coolerH);
        }
      }
    }

    return products;
  }

  /* ============================================================
     USER FILTER APPLICATION
     ============================================================ */
  function applyUserFilters(products, stepIndex) {
    const step = STEPS[stepIndex];
    const f = state.filters[step.key] || {};
    const stepDef = STEP_FILTERS[step.key] || {};

    // Tile filter (when active as string)
    if (stepDef.tiles && f[stepDef.tiles.key]) {
      const tileVal = f[stepDef.tiles.key];
      if (typeof tileVal === 'string' && tileVal) {
        products = products.filter(p => getMeta(p, stepDef.tiles.key) === tileVal);
      } else if (Array.isArray(tileVal) && tileVal.length > 0) {
        products = products.filter(p => tileVal.includes(getMeta(p, stepDef.tiles.key)));
      }
    }

    // Checkbox filters
    (stepDef.checkboxes || []).forEach(cb => {
      // If this key is currently active via single string from tiles, let tiles handle it
      if (stepDef.tiles && stepDef.tiles.key === cb.key && typeof f[cb.key] === 'string') return;

      const active = f[cb.key];
      if (active && Array.isArray(active) && active.length > 0) {
        products = products.filter(p => {
          const val = String(getMeta(p, cb.key) || '').trim();
          if (val.includes(',')) {
            const vals = val.split(',').map(s => s.trim());
            return active.some(a => vals.includes(a));
          }
          return active.includes(val);
        });
      }
    });

    // Price filter
    if (f.price_min !== undefined || f.price_max !== undefined) {
      const min = parseFloat(f.price_min || 0);
      const max = parseFloat(f.price_max || 9999999);
      products = products.filter(p => {
        const price = getPrice(p);
        return price >= min && price <= max;
      });
    }

    return products;
  }

  /* ============================================================
     SORTING
     ============================================================ */
  function applySort(products) {
    if (state.sort === 'price-asc') {
      return [...products].sort((a, b) => getPrice(a) - getPrice(b));
    }
    if (state.sort === 'price-desc') {
      return [...products].sort((a, b) => getPrice(b) - getPrice(a));
    }
    return products;
  }

  /* ============================================================
     RENDER PRODUCTS
     ============================================================ */
  function renderProducts(stepIndex) {
    const step = STEPS[stepIndex];
    let products = getCompatibilityFiltered(stepIndex);
    products = applyUserFilters(products, stepIndex);

    // Sort
    products = applySort(products);

    els.resultCount.textContent = `${products.length} product${products.length !== 1 ? 's' : ''}`;

    if (products.length === 0) {
      showEmpty();
      return;
    }

    hideStates();

    const page = state.pages[step.key] || 1;
    const paged = products.slice(0, page * PAGE_SIZE);
    const hasMore = paged.length < products.length;

    els.grid.innerHTML = paged.map(p => renderCard(p, step)).join('');
    els.loadMoreWrap.hidden = !hasMore;

    // Rebind card clicks
    els.grid.querySelectorAll('.bpc-card').forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('[data-action="ram-dec"]')) {
          e.stopPropagation();
          setRamQty((state.ramQty || 1) - 1);
          return;
        }
        if (e.target.closest('[data-action="ram-inc"]')) {
          e.stopPropagation();
          setRamQty((state.ramQty || 1) + 1);
          return;
        }
        if (e.target.closest('a') || e.target.closest('.bpc-ram-qty-ctrl')) return;
        const productId = parseInt(card.dataset.id);
        const product = products.find(p => p.id === productId);
        if (product) selectProduct(product, step.key, stepIndex);
      });
    });
  }

  function getMotherboardRamSlots(mbProduct) {
    if (!mbProduct) return 4;
    const explicitMeta = getMeta(mbProduct, 'ram_slots');
    if (explicitMeta) {
      const n = parseInt(explicitMeta);
      if (n > 0) return n;
    }
    const title = (mbProduct.title || '').toUpperCase();
    const desc = ((mbProduct.description || '') + ' ' + (mbProduct.body_html || '')).toUpperCase();
    const text = title + ' ' + desc;

    if (text.includes('4 X DIMM') || text.includes('4X DIMM') || text.includes('4 DIMM') || text.includes('4-DIMM') || text.includes('QUAD-CHANNEL')) {
      return 4;
    }
    if (text.includes('2 X DIMM') || text.includes('2X DIMM') || text.includes('2 DIMM') || text.includes('2-DIMM') || text.includes('2 X DDR') || text.includes('2X DDR') || text.includes('ITX') || text.includes('MINI-ITX')) {
      return 2;
    }
    // Specific 2-DIMM models & chipsets
    if (
      title.includes('H610M-K') || title.includes('H610M-E') || 
      title.includes('B650M-E') || title.includes('B650M-B') || 
      title.includes('A520M') || title.includes('A620M-E') || 
      title.includes('H510M') || title.includes('H410M') || 
      title.includes('H310M') || title.includes('A320M') || 
      title.includes('PRO-M2')
    ) {
      return 2;
    }
    return 4;
  }

  function setRamQty(qty) {
    const maxSlots = getMotherboardRamSlots(state.selections.motherboard);
    const clamped = Math.max(1, Math.min(qty, maxSlots));
    state.ramQty = clamped;
    saveToStorage();
    renderProducts(state.currentStep);
    renderSummary();
  }

  function renderCard(product, step) {
    const isSelected = state.selections[step.key] && state.selections[step.key].id === product.id;
    const price = getPrice(product);
    const imgSrc = product.images && product.images[0]
      ? shopifyImgSize(product.images[0].src, 300)
      : '';
    const brand = getMeta(product, 'brand') || product.vendor || '';
    const badge = getSmpsBadge(product, step.key);

    let ramQtyCtrl = '';
    if (step.key === 'ram' && isSelected) {
      const maxSlots = getMotherboardRamSlots(state.selections.motherboard);
      const curQty = state.ramQty || 1;
      const mbText = state.selections.motherboard
        ? `Motherboard has ${maxSlots} RAM slots (Max ${maxSlots} modules)`
        : `Motherboard limit: up to ${maxSlots} modules`;
      ramQtyCtrl = `
        <div class="bpc-ram-qty-ctrl" onclick="event.stopPropagation();">
          <div class="bpc-ram-qty-row">
            <span class="bpc-ram-qty-label">RAM Modules:</span>
            <div class="bpc-ram-qty-actions">
              <button type="button" class="bpc-ram-qty-btn bpc-ram-qty-dec" data-action="ram-dec" ${curQty <= 1 ? 'disabled' : ''} aria-label="Decrease modules">−</button>
              <span class="bpc-ram-qty-val">${curQty}</span>
              <button type="button" class="bpc-ram-qty-btn bpc-ram-qty-inc" data-action="ram-inc" ${curQty >= maxSlots ? 'disabled' : ''} aria-label="Increase modules">+</button>
            </div>
          </div>
          <div class="bpc-ram-slot-note">${mbText}</div>
        </div>`;
    }

    const productUrl = product.handle ? `/products/${product.handle}` : '';
    return `
      <div class="bpc-card${isSelected ? ' selected' : ''}" data-id="${product.id}">
        <div class="bpc-card-check">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <a class="bpc-card-img" href="${productUrl}" target="_blank" rel="noopener" tabindex="-1" aria-label="View ${escHtml(product.title)}">
          ${imgSrc ? `<img src="${imgSrc}" alt="${escHtml(product.title)}" loading="lazy" width="300" height="300">` : '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>'}
        </a>
        <div class="bpc-card-info">
          ${brand ? `<div class="bpc-card-brand">${escHtml(brand)}</div>` : ''}
          ${productUrl ? `<a class="bpc-card-title" href="${productUrl}" target="_blank" rel="noopener">${escHtml(product.title)}</a>` : `<div class="bpc-card-title">${escHtml(product.title)}</div>`}
          <div class="bpc-card-price">${CURRENCY}${formatPrice(price)}</div>
          ${badge}
        </div>
        ${ramQtyCtrl}
        <button class="bpc-card-btn" data-id="${product.id}">${isSelected ? 'Selected' : 'Select'}</button>
      </div>`;
  }

  function getSmpsBadge(product, stepKey) {
    if (stepKey === 'smps' && state.selections.gpu) {
      const minWattage = getGpuMinPsuWattage(state.selections.gpu);
      if (minWattage > 0) {
        const psuW = parseInt(getMeta(product, 'wattage') || 0);
        if (psuW >= minWattage) {
          const gpuShort = state.selections.gpu.title.replace(/Graphics Card|Graphic Card|Gaming|ZOTAC|MSI|GeForce|GDDR\d/gi, '').trim().split(/\s+/).slice(0, 3).join(' ');
          return `<div style="font-size:1.1rem;color:#16a34a;font-weight:600;margin-top:0.4rem;">✓ Recommended for ${escHtml(gpuShort)} (${minWattage}W+)</div>`;
        }
      }
    }
    return '';
  }

  /* ============================================================
     SELECT PRODUCT
     ============================================================ */
  function selectProduct(product, stepKey, stepIndex) {
    const wasSelected = state.selections[stepKey] && state.selections[stepKey].id === product.id;

    if (wasSelected) {
      // Deselecting — clear all downstream unconditionally
      delete state.selections[stepKey];
      resetDownstream(stepKey, null);
      if (stepKey === 'ram') {
        state.ramQty = 1;
      }
    } else {
      if (state.selections[stepKey] && state.selections[stepKey].id !== product.id) {
        // Changing selection — smart reset: only clear incompatible downstream
        resetDownstream(stepKey, product);
      }
      state.selections[stepKey] = product;

      if (stepKey === 'motherboard') {
        const maxSlots = getMotherboardRamSlots(product);
        if ((state.ramQty || 1) > maxSlots) {
          state.ramQty = maxSlots;
        }
      }
      if (stepKey === 'ram') {
        const maxSlots = getMotherboardRamSlots(state.selections.motherboard);
        if (!state.ramQty || state.ramQty < 1) state.ramQty = 1;
        if (state.ramQty > maxSlots) state.ramQty = maxSlots;
      }
    }

    saveToStorage();
    renderProducts(stepIndex);
    renderStepTabs();
    renderSummary();
  }

  const DOWNSTREAM = {
    cpu: ['motherboard', 'ram', 'primary_storage', 'secondary_storage', 'storage', 'additional_storage', 'ssd', 'cooler', 'cabinet'],
    motherboard: ['ram', 'primary_storage', 'secondary_storage', 'storage', 'additional_storage', 'ssd', 'cabinet'],
    gpu: ['smps', 'cabinet'],
    cooler: ['cabinet']
  };

  /**
   * Check whether a previously selected downstream component is still
   * compatible with the newly selected upstream component.
   * Returns true  → keep the selection
   * Returns false → clear it
   */
  function isStillCompatible(upstreamKey, newProduct, downstreamKey) {
    const downSel = state.selections[downstreamKey];
    if (!downSel) return true; // nothing selected downstream — nothing to clear

    if (upstreamKey === 'cpu') {
      const cpuSocket = (getMeta(newProduct, 'socket_type') || '').toUpperCase().replace(/\s+/g, '');
      if (downstreamKey === 'motherboard' && cpuSocket) {
        const mbSocket = (getMeta(downSel, 'socket_type') || '').toUpperCase().replace(/\s+/g, '');
        return mbSocket === cpuSocket;
      }
      if (downstreamKey === 'cooler' && cpuSocket) {
        const compat = getMeta(downSel, 'socket_compatibility') || '';
        const sockets = compat.split(',').map(s => s.trim().toUpperCase().replace(/\s+/g, '')).filter(Boolean);
        return sockets.includes(cpuSocket);
      }
      // RAM, SSD, cabinet — no direct CPU constraint
      return true;
    }

    if (upstreamKey === 'motherboard') {
      if (downstreamKey === 'ram') {
        const mbRamType = (getMeta(newProduct, 'ram_type') || '').toUpperCase().replace(/\s+/g, '');
        if (mbRamType) {
          const ramType = (getMeta(downSel, 'ram_type') || '').toUpperCase().replace(/\s+/g, '');
          return ramType === mbRamType;
        }
        return true;
      }
      if (downstreamKey === 'cabinet') {
        const ff = (getMeta(newProduct, 'form_factor') || '').toUpperCase().replace(/\s+/g, '');
        if (ff) {
          const ffs = (getMeta(downSel, 'supported_form_factors') || '').toUpperCase().replace(/\s+/g, '');
          const formFactors = ffs.split(',').map(s => s.trim()).filter(Boolean);
          return formFactors.includes(ff);
        }
        return true;
      }
      // SSD — no direct motherboard compatibility constraint
      return true;
    }

    if (upstreamKey === 'cooler') {
      if (downstreamKey === 'cabinet') {
        const coolerType = getMeta(newProduct, 'cooler_type');
        if (coolerType === 'AIO Liquid') {
          const aioSize = parseInt(getMeta(newProduct, 'aio_size_mm') || 0);
          if (aioSize) {
            const rads = (getMeta(downSel, 'radiator_support') || '120,240,360').split(',').map(s => parseInt(s.trim())).filter(Boolean);
            return rads.includes(aioSize);
          }
        } else {
          const coolerH = parseInt(getMeta(newProduct, 'height_mm') || 0);
          if (coolerH) {
            return parseInt(getMeta(downSel, 'max_cooler_height_mm') || 9999) >= coolerH;
          }
        }
        return true;
      }
      return true;
    }

    if (upstreamKey === 'gpu') {
      if (downstreamKey === 'smps') {
        const minWattage = getGpuMinPsuWattage(newProduct);
        if (minWattage > 0) {
          const psuW = parseInt(getMeta(downSel, 'wattage') || 0);
          return psuW >= minWattage;
        }
        return true;
      }
      if (downstreamKey === 'cabinet') {
        const gpuLen = parseInt(getMeta(newProduct, 'length_mm') || 0);
        if (gpuLen) {
          return parseInt(getMeta(downSel, 'max_gpu_length_mm') || 9999) >= gpuLen;
        }
        return true;
      }
      return true;
    }

    return true;
  }

  function resetDownstream(stepKey, newProduct) {
    const downstream = DOWNSTREAM[stepKey] || [];
    downstream.forEach(key => {
      // If newProduct provided, only clear selections that are truly incompatible.
      // If newProduct is absent (deselect case), clear everything downstream.
      if (newProduct && isStillCompatible(stepKey, newProduct, key)) {
        // Selection is still compatible — keep it, but mark as needing re-fetch
        // so compatibility filters re-apply on the product list.
        delete state.fetched[key];
        if (key === 'ram' && stepKey === 'motherboard') {
          const maxSlots = getMotherboardRamSlots(newProduct);
          if ((state.ramQty || 1) > maxSlots) {
            state.ramQty = maxSlots;
          }
        }
      } else {
        delete state.selections[key];
        delete state.fetched[key];
        if (key === 'ram') {
          state.ramQty = 1;
        }
      }
    });
  }

  /* ============================================================
     FILTERS UI
     ============================================================ */
  function renderFilters(stepIndex) {
    const step = STEPS[stepIndex];
    const stepDef = STEP_FILTERS[step.key] || {};
    const f = state.filters[step.key] || {};
    let html = '';

    // Checkbox groups
    (stepDef.checkboxes || []).forEach(cb => {
      // Hide AIO size if not AIO selected
      if (cb.conditional) {
        const [condKey, condVal] = cb.conditional.split(':');
        if (f[condKey] !== condVal) return;
      }
      // Skip if no values yet (products not fetched)
      if (!cb.values || cb.values.length === 0) return;
      const activeVals = f[cb.key] || [];
      html += `
        <div class="bpc-filter-group is-open" data-group="${cb.key}">
          <div class="bpc-filter-group-header">
            <span>${cb.label}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="18 15 12 9 6 15"/></svg>
          </div>
          <div class="bpc-filter-group-body">
            ${cb.values.map((val, i) => {
        let label = (cb.labels && cb.labels[i]) ? cb.labels[i] : (cb.suffix ? val + cb.suffix : val);
        if (cb.key === 'capacity_gb') {
          const num = parseInt(val);
          if (num >= 1000) {
            label = (num / 1000) + 'TB';
          } else {
            label = num + 'GB';
          }
        }
        if (cb.key === 'interface') {
          if (val === 'HDD') label = 'Hard Disk (HDD)';
          else if (val === 'NVMe') label = 'NVMe M.2 SSD';
          else if (val === 'SATA') label = 'SATA SSD';
        }
        const checked = Array.isArray(activeVals) ? activeVals.includes(String(val)) : (activeVals === String(val) ? 'checked' : '');
        return `<label class="bpc-checkbox-item">
                <input type="checkbox" data-filter-key="${cb.key}" value="${val}" ${checked ? 'checked' : ''}>
                <span>${label}</span>
              </label>`;
      }).join('')}
          </div>
        </div>`;
    });

    // Price range
    if (stepDef.price) {
      const allPrices = (state.allProducts[step.key] || []).map(p => getPrice(p)).filter(Boolean);
      const globalMin = allPrices.length ? Math.floor(Math.min(...allPrices)) : 0;
      const globalMax = allPrices.length ? Math.ceil(Math.max(...allPrices)) : 999999;
      const curMin = f.price_min !== undefined ? f.price_min : globalMin;
      const curMax = f.price_max !== undefined ? f.price_max : globalMax;
      html += `
        <div class="bpc-filter-group is-open" data-group="price">
          <div class="bpc-filter-group-header">
            <span>Price</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="18 15 12 9 6 15"/></svg>
          </div>
          <div class="bpc-filter-group-body">
            <div class="bpc-price-range">
              <div class="bpc-range-inputs">
                <input class="bpc-range-input" type="number" id="bpcPriceMin" value="${curMin}" min="${globalMin}" max="${globalMax}" placeholder="Min">
                <input class="bpc-range-input" type="number" id="bpcPriceMax" value="${curMax}" min="${globalMin}" max="${globalMax}" placeholder="Max">
              </div>
              <div class="bpc-range-track">
                <div class="bpc-range-fill" id="bpcRangeFill"></div>
                <input class="bpc-range-slider" type="range" id="bpcSliderMin" min="${globalMin}" max="${globalMax}" value="${curMin}">
                <input class="bpc-range-slider" type="range" id="bpcSliderMax" min="${globalMin}" max="${globalMax}" value="${curMax}">
              </div>
            </div>
          </div>
        </div>`;
    }

    els.filtersBody.innerHTML = html || '<p style="padding:1.6rem;opacity:0.5;font-size:1.3rem;">No filters available</p>';
    bindFilterEvents(stepIndex);
    updateRangeFill();
  }

  function bindFilterEvents(stepIndex) {
    const step = STEPS[stepIndex];

    // Collapsible groups
    els.filtersBody.querySelectorAll('.bpc-filter-group-header').forEach(header => {
      header.addEventListener('click', () => {
        header.closest('.bpc-filter-group').classList.toggle('is-open');
      });
    });

    // Checkboxes
    els.filtersBody.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      cb.addEventListener('change', () => {
        const key = cb.dataset.filterKey;
        const val = cb.value;
        if (!state.filters[step.key]) state.filters[step.key] = {};
        let arr = state.filters[step.key][key] || [];
        if (cb.checked) {
          arr = [...arr, String(val)];
        } else {
          arr = arr.filter(v => v !== String(val));
        }
        state.filters[step.key][key] = arr;
        debouncedRender(stepIndex);
        renderActiveFilters(stepIndex);
      });
    });

    // Price inputs
    const minInput = $('bpcPriceMin');
    const maxInput = $('bpcPriceMax');
    const sliderMin = $('bpcSliderMin');
    const sliderMax = $('bpcSliderMax');

    if (minInput) {
      minInput.addEventListener('input', () => {
        if (!state.filters[step.key]) state.filters[step.key] = {};
        state.filters[step.key].price_min = parseFloat(minInput.value) || 0;
        if (sliderMin) sliderMin.value = minInput.value;
        updateRangeFill();
        debouncedRender(stepIndex);
      });
    }
    if (maxInput) {
      maxInput.addEventListener('input', () => {
        if (!state.filters[step.key]) state.filters[step.key] = {};
        state.filters[step.key].price_max = parseFloat(maxInput.value) || 9999999;
        if (sliderMax) sliderMax.value = maxInput.value;
        updateRangeFill();
        debouncedRender(stepIndex);
      });
    }
    if (sliderMin) {
      sliderMin.addEventListener('input', () => {
        if (parseFloat(sliderMin.value) > parseFloat(sliderMax.value)) sliderMin.value = sliderMax.value;
        if (minInput) minInput.value = sliderMin.value;
        if (!state.filters[step.key]) state.filters[step.key] = {};
        state.filters[step.key].price_min = parseFloat(sliderMin.value);
        updateRangeFill();
        debouncedRender(stepIndex);
      });
    }
    if (sliderMax) {
      sliderMax.addEventListener('input', () => {
        if (parseFloat(sliderMax.value) < parseFloat(sliderMin.value)) sliderMax.value = sliderMin.value;
        if (maxInput) maxInput.value = sliderMax.value;
        if (!state.filters[step.key]) state.filters[step.key] = {};
        state.filters[step.key].price_max = parseFloat(sliderMax.value);
        updateRangeFill();
        debouncedRender(stepIndex);
      });
    }
  }

  function updateRangeFill() {
    const sliderMin = $('bpcSliderMin');
    const sliderMax = $('bpcSliderMax');
    const fill = $('bpcRangeFill');
    if (!sliderMin || !sliderMax || !fill) return;
    const min = parseFloat(sliderMin.min);
    const max = parseFloat(sliderMin.max);
    const valMin = parseFloat(sliderMin.value);
    const valMax = parseFloat(sliderMax.value);
    const leftPct = ((valMin - min) / (max - min)) * 100;
    const rightPct = ((valMax - min) / (max - min)) * 100;
    fill.style.left = leftPct + '%';
    fill.style.width = (rightPct - leftPct) + '%';
  }

  function renderActiveFilters(stepIndex) {
    const step = STEPS[stepIndex];
    const f = state.filters[step.key] || {};
    const chips = [];

    Object.entries(f).forEach(([key, val]) => {
      if (key === 'price_min' || key === 'price_max') return;
      if (Array.isArray(val)) {
        val.forEach(v => {
          let label = v;
          if (key === 'radiator_support') label = v + 'mm Radiator';
          else if (key === 'capacity_gb') label = parseInt(v) >= 1000 ? (parseInt(v)/1000) + 'TB' : v + 'GB';
          else if (key === 'interface') {
            if (v === 'HDD') label = 'Hard Disk (HDD)';
            else if (v === 'NVMe') label = 'NVMe SSD';
            else if (v === 'SATA') label = 'SATA SSD';
          }
          chips.push({ key, val: v, label });
        });
      } else if (val) {
        let label = val;
        if (key === 'interface') {
          if (val === 'HDD') label = 'Hard Disk (HDD)';
          else if (val === 'NVMe') label = 'NVMe SSD';
          else if (val === 'SATA') label = 'SATA SSD';
        }
        chips.push({ key, val, label });
      }
    });

    els.activeFilters.innerHTML = chips.map(chip => `
      <span class="bpc-chip">
        ${escHtml(chip.label)}
        <button data-key="${chip.key}" data-val="${chip.val}" aria-label="Remove filter">&times;</button>
      </span>`).join('');

    els.activeFilters.querySelectorAll('button').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        const val = btn.dataset.val;
        if (!state.filters[step.key]) return;
        if (Array.isArray(state.filters[step.key][key])) {
          state.filters[step.key][key] = state.filters[step.key][key].filter(v => v !== val);
        } else {
          delete state.filters[step.key][key];
        }
        renderFilters(stepIndex);
        renderActiveFilters(stepIndex);
        debouncedRender(stepIndex);
      });
    });
  }

  /* ============================================================
     TILE FILTERS
     ============================================================ */
  function renderTileFilters(stepIndex) {
    const step = STEPS[stepIndex];
    const stepDef = STEP_FILTERS[step.key] || {};
    const f = state.filters[step.key] || {};

    if (!stepDef.tiles) {
      els.tileFilters.innerHTML = '';
      els.tileFilters.hidden = true;
      return;
    }

    els.tileFilters.hidden = false;
    const active = f[stepDef.tiles.key] || '';

    // If values not yet populated (fetch pending), hide tiles for now
    let values = stepDef.tiles.values || [];
    if (values.length === 0) {
      els.tileFilters.innerHTML = '';
      els.tileFilters.hidden = true;
      return;
    }

    // For Storage: hide NVMe if no NVMe slots on motherboard
    if ((step.key === 'primary_storage' || step.key === 'secondary_storage' || step.key === 'storage' || step.key === 'additional_storage' || step.key === 'ssd') && state.selections.motherboard) {
      const nvme = parseInt(getMeta(state.selections.motherboard, 'nvme_slots') || 0);
      if (nvme === 0) values = values.filter(v => v !== 'NVMe');
    }

    els.tileFilters.innerHTML = `
      <div style="font-size:1.2rem;font-weight:600;color:var(--color-text);opacity:0.7;width:100%;">${stepDef.tiles.label}</div>
      ${values.map(val => `
        <button class="bpc-tile${active === val ? ' active' : ''}" data-tile-key="${stepDef.tiles.key}" data-tile-val="${val}">
          ${escHtml(val)}
        </button>`).join('')}`;

    els.tileFilters.querySelectorAll('.bpc-tile').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.tileKey;
        const val = btn.dataset.tileVal;
        if (!state.filters[step.key]) state.filters[step.key] = {};
        if (state.filters[step.key][key] === val) {
          delete state.filters[step.key][key];
        } else {
          state.filters[step.key][key] = val;
        }
        renderTileFilters(stepIndex);
        renderFilters(stepIndex); // re-render to show/hide conditional filters
        renderProducts(stepIndex);
        renderActiveFilters(stepIndex);
      });
    });
  }

  /* ============================================================
     SUMMARY
     ============================================================ */
  function renderSummary() {
    let totalPrice = 0;
    let totalWattage = 50; // base

    const maxRamSlots = getMotherboardRamSlots(state.selections.motherboard);
    const ramQty = state.ramQty || 1;

    const rows = STEPS.map(step => {
      const sel = state.selections[step.key];
      const qty = (step.key === 'ram' && sel) ? ramQty : 1;
      const unitPrice = sel ? getPrice(sel) : 0;
      const stepTotal = unitPrice * qty;
      totalPrice += stepTotal;

      if (step.key === 'cpu' && sel) totalWattage += parseInt(getMeta(sel, 'tdp_watts') || 0);
      if (step.key === 'gpu' && sel) totalWattage += parseInt(getMeta(sel, 'tdp_watts') || 0);

      const imgSrc = sel && sel.images && sel.images[0]
        ? shopifyImgSize(sel.images[0].src, 100)
        : '';
      const hasSelection = !!sel;

      let ramSummaryQtyCtrl = '';
      if (step.key === 'ram' && hasSelection) {
        ramSummaryQtyCtrl = `
          <div class="bpc-summary-ram-ctrl" onclick="event.stopPropagation();">
            <span class="bpc-summary-ram-lbl">Qty:</span>
            <button type="button" class="bpc-summary-ram-btn" data-action="summary-ram-dec" ${ramQty <= 1 ? 'disabled' : ''} aria-label="Decrease RAM modules">−</button>
            <span class="bpc-summary-ram-count">${ramQty}</span>
            <button type="button" class="bpc-summary-ram-btn" data-action="summary-ram-inc" ${ramQty >= maxRamSlots ? 'disabled' : ''} aria-label="Increase RAM modules">+</button>
            <span class="bpc-summary-ram-max">/ ${maxRamSlots} slots</span>
          </div>`;
      }

      return `
        <div class="bpc-summary-item${step.key === 'ram' ? ' bpc-summary-item--ram' : ''}">
          <div class="bpc-summary-item-img">
            ${imgSrc ? `<img src="${imgSrc}" alt="" loading="lazy" width="44" height="44">` : '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>'}
          </div>
          <div class="bpc-summary-item-info">
            <div class="bpc-summary-item-label">${step.label}${step.key === 'ram' && hasSelection ? ` (${ramQty}x)` : ''}</div>
            <div class="bpc-summary-item-name${hasSelection ? '' : ' empty'}">${hasSelection ? escHtml(sel.title) : 'Not selected yet'}</div>
            ${ramSummaryQtyCtrl}
          </div>
          ${hasSelection ? `<div class="bpc-summary-item-price">${CURRENCY}${formatPrice(stepTotal)}</div>` : ''}
          <button class="bpc-summary-item-remove" data-step="${step.key}" aria-label="Remove ${step.label}" ${hasSelection ? '' : 'hidden'}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>`;
    }).join('');

    els.summaryBody.innerHTML = rows;

    // Remove buttons
    els.summaryBody.querySelectorAll('.bpc-summary-item-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const stepKey = btn.dataset.step;
        delete state.selections[stepKey];
        if (stepKey === 'ram') state.ramQty = 1;
        resetDownstream(stepKey);
        saveToStorage();
        renderSummary();
        // Navigate to the step that was removed
        const stepIndex = STEPS.findIndex(s => s.key === stepKey);
        if (stepIndex !== -1) goToStep(stepIndex);
      });
    });

    // Summary RAM stepper buttons
    els.summaryBody.querySelectorAll('[data-action="summary-ram-dec"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        setRamQty((state.ramQty || 1) - 1);
      });
    });
    els.summaryBody.querySelectorAll('[data-action="summary-ram-inc"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        setRamQty((state.ramQty || 1) + 1);
      });
    });

    // Totals
    const priceFormatted = CURRENCY + formatPrice(totalPrice);
    els.totalPrice.textContent = priceFormatted;
    els.mobileTotal.textContent = priceFormatted;

    // Wattage
    const hasTdp = state.selections.cpu || state.selections.gpu;
    els.summaryWattage.hidden = !hasTdp;
    if (hasTdp) {
      els.wattageText.textContent = `Estimated Power: ${totalWattage}W`;
      els.mobileWattage.textContent = `~${totalWattage}W`;
    } else {
      els.mobileWattage.textContent = '';
    }

    // Add to cart button
    const hasAny = Object.keys(state.selections).length > 0;
    els.addToCart.disabled = !hasAny;
    els.mobileAddToCart.disabled = !hasAny;
  }

  /* ============================================================
     ADD TO CART
     ============================================================ */
  let _cartAdding = false;
  async function addAllToCart() {
    if (_cartAdding) return;
    const items = Object.entries(state.selections).map(([stepKey, p]) => ({
      id: p.variants[0].id,
      quantity: stepKey === 'ram' ? (state.ramQty || 1) : 1
    }));

    if (!items.length) return;
    _cartAdding = true;

    const btn = els.addToCart;
    const mobileBtn = els.mobileAddToCart;
    btn.disabled = true;
    mobileBtn.disabled = true;
    btn.textContent = 'Adding...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('Cart add failed:', JSON.stringify(err));
        // Surface a helpful out-of-stock message to the user
        const errMsg = err && (err.description || err.message || '');
        const outOfStockMatch = errMsg.match(/The product '(.+?)' is already sold out/);
        if (outOfStockMatch) {
          throw new Error(`Stock not available for "${outOfStockMatch[1]}". Please remove it from your build and choose an alternative.`);
        }
        throw new Error(errMsg || 'Could not add items to cart. Please try again.');
      }

      // Trigger minicart open if theme supports it
      document.dispatchEvent(new CustomEvent('cart:open'));
      // Also update cart count via refresh
      fetch('/cart.js').then(r => r.json()).then(cart => {
        const countEl = document.querySelector('.cart-count, .js-cart-count, [data-cart-count]');
        if (countEl) countEl.textContent = cart.item_count;
      });

      btn.textContent = 'Added!';
      setTimeout(() => {
        btn.textContent = 'Add All to Cart';
        btn.disabled = false;
        mobileBtn.disabled = false;
      }, 2000);
    } catch (e) {
      // Show the descriptive error to the user
      const msg = e.message || 'Could not add items to cart. Please try again.';
      alert(msg);
      btn.textContent = 'Error — Try Again';
      btn.disabled = false;
      mobileBtn.disabled = false;
      setTimeout(() => { btn.textContent = 'Add All to Cart'; }, 3000);
    } finally {
      _cartAdding = false;
    }
  }

  /* ============================================================
     EVENT BINDINGS
     ============================================================ */
  function bindEvents() {
    // Step tabs
    els.stepsTabs.addEventListener('click', (e) => {
      const tab = e.target.closest('.bpc-step-tab');
      if (!tab) return;
      const stepIndex = parseInt(tab.dataset.step);
      if (!canAccessStep(stepIndex)) return;
      goToStep(stepIndex);
    });

    // Prev / Next
    els.prevStep.addEventListener('click', () => goToStep(state.currentStep - 1));
    els.nextStep.addEventListener('click', () => {
      if (state.currentStep < STEPS.length - 1) goToStep(state.currentStep + 1);
    });

    // Filter open/close (mobile)
    els.filterToggle.addEventListener('click', openFilters);
    els.filtersClose.addEventListener('click', closeFilters);
    els.filtersOverlay.addEventListener('click', closeFilters);
    els.applyFilters.addEventListener('click', closeFilters);

    if (els.sortBy) {
      els.sortBy.addEventListener('change', () => {
        state.sort = els.sortBy.value;
        const step = STEPS[state.currentStep];
        state.pages[step.key] = 1;
        saveToStorage();
        renderProducts(state.currentStep);
      });
    }

    // Clear filters
    els.clearFilters.addEventListener('click', () => {
      const stepKey = STEPS[state.currentStep].key;
      state.filters[stepKey] = {};
      renderFilters(state.currentStep);
      renderTileFilters(state.currentStep);
      renderActiveFilters(state.currentStep);
      renderProducts(state.currentStep);
    });
    els.resetFilters && els.resetFilters.addEventListener('click', () => {
      const stepKey = STEPS[state.currentStep].key;
      state.filters[stepKey] = {};
      renderFilters(state.currentStep);
      renderTileFilters(state.currentStep);
      renderProducts(state.currentStep);
    });

    // Load More
    els.loadMore.addEventListener('click', () => {
      const stepKey = STEPS[state.currentStep].key;
      state.pages[stepKey] = (state.pages[stepKey] || 1) + 1;
      renderProducts(state.currentStep);
    });

    // Retry
    els.retry && els.retry.addEventListener('click', () => {
      delete state.fetched[STEPS[state.currentStep].key];
      fetchAndRenderStep(state.currentStep);
    });

    // Add to cart
    els.addToCart.addEventListener('click', addAllToCart);
    els.mobileAddToCart.addEventListener('click', addAllToCart);

    // Mobile: view build opens summary
    els.mobileViewBuild.addEventListener('click', () => {
      els.summary.classList.remove('is-collapsed');
      els.summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // Summary collapse toggle
    els.summaryToggle.addEventListener('click', () => {
      els.summary.classList.toggle('is-collapsed');
    });
  }

  function openFilters() {
    els.filters.classList.add('is-open');
    els.filtersOverlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }
  function closeFilters() {
    els.filters.classList.remove('is-open');
    els.filtersOverlay.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  /* ============================================================
     STATE HELPERS
     ============================================================ */
  function showSkeletons() {
    els.grid.innerHTML = Array(8).fill(`
      <div class="bpc-skeleton-card">
        <div class="bpc-skeleton bpc-skeleton--image"></div>
        <div class="bpc-skeleton bpc-skeleton--title"></div>
        <div class="bpc-skeleton bpc-skeleton--price"></div>
        <div class="bpc-skeleton bpc-skeleton--btn"></div>
      </div>`).join('');
    els.empty.hidden = true;
    els.error.hidden = true;
    els.loadMoreWrap.hidden = true;
  }
  function showEmpty() {
    els.grid.innerHTML = '';
    els.empty.hidden = false;
    els.error.hidden = true;
    els.loadMoreWrap.hidden = true;
    els.resultCount.textContent = '0 products';
  }
  function showError() {
    els.grid.innerHTML = '';
    els.empty.hidden = true;
    els.error.hidden = false;
    els.loadMoreWrap.hidden = true;
  }
  function hideStates() {
    els.empty.hidden = true;
    els.error.hidden = true;
  }

  function getGpuMinPsuWattage(gpuProduct) {
    if (!gpuProduct) return 0;
    const title = (gpuProduct.title || '').toUpperCase();
    
    // Exact user requirement table:
    if (title.includes('5090')) return 1000;
    if (title.includes('5080') || title.includes('4090')) return 850;
    if (title.includes('5070 TI') || title.includes('5070TI')) return 750;
    if ((title.includes('5060 TI') || title.includes('5060TI')) && (title.includes('16G') || title.includes('16GB'))) return 750;
    if (title.includes('4080')) return 750;
    
    if (title.includes('5070')) return 650;
    if (title.includes('5060 TI') || title.includes('5060TI')) return 650;
    if (title.includes('5060')) return 650;
    if (title.includes('5050')) return 650;
    if (title.includes('3060')) return 650;
    if (title.includes('4070')) return 650;
    
    if (title.includes('3050') && (title.includes('8G') || title.includes('8GB'))) return 550;
    if (title.includes('4060')) return 550;
    
    if (title.includes('710') || title.includes('730') || (title.includes('3050') && (title.includes('6G') || title.includes('6GB')))) {
      return 0;
    }
    
    if (title.includes('RTX') || title.includes('RX ') || title.includes('GRAPHICS')) {
      return 550;
    }
    
    return 0;
  }

  /* ============================================================
     UTILITIES
     ============================================================ */
  function shopifyImgSize(url, size) {
    if (!url) return '';
    // Storefront API CDN URLs support ?width= param
    const base = url.split('?')[0];
    return `${base}?width=${size}`;
  }

  function isProductValidForStep(product, stepKey) {
    if (!product) return false;
    const title = (product.title || '').toLowerCase();
    if (stepKey === 'primary_storage' || stepKey === 'secondary_storage' || stepKey === 'ssd' || stepKey === 'storage' || stepKey === 'additional_storage') {
      if (title.includes('motherboard') || title.includes('b650') || title.includes('z790') || title.includes('b760') || title.includes('x670') || title.includes('wifi atx') || title.includes('wifi m-atx') || title.includes('wifi matx') || title.includes('godlike')) return false;
      if (title.includes('laptop ram') || title.includes('desktop ram') || title.includes('ddr4') || title.includes('ddr5')) return false;
    }
    if (stepKey === 'gpu') {
      if (title.includes('ram') || title.includes('ddr4') || title.includes('ddr5') || title.includes('memory') || title.includes('premier') || title.includes('gammix')) {
        if (!title.includes('graphics') && !title.includes('geforce') && !title.includes('radeon') && !title.includes('rtx') && !title.includes('gtx')) {
          return false;
        }
      }
      if (title.includes('motherboard')) return false;
    }
    return true;
  }

  function getMeta(product, key) {
    if (!product) return null;
    let val = null;
    if (product.metafields && Array.isArray(product.metafields)) {
      const meta = product.metafields.filter(Boolean).find(m => m.namespace === 'pcb' && m.key === key);
      if (meta && meta.value !== undefined && meta.value !== null) val = String(meta.value).trim();
    }

    const title = product.title || '';
    const titleUpper = title.toUpperCase();

    // Normalisation & Fallbacks per Buil_PC_Guide .pdf
    if (key === 'brand') {
      let b = val ? String(val).trim() : (product.vendor ? product.vendor.trim() : '');
      const bUpper = (b || title).toUpperCase();
      if (bUpper.includes('WESTERN DIGITAL') || bUpper.includes('WD')) return 'Western Digital';
      if (bUpper.includes('CORSAIR')) return 'CORSAIR';
      if (bUpper.includes('ADATA') || bUpper.includes('XPG')) return 'ADATA';
      if (bUpper.includes('MSI')) return 'MSI';
      if (bUpper.includes('ASUS')) return 'ASUS';
      if (bUpper.includes('DEEPCOOL')) return 'Deepcool';
      if (bUpper.includes('ZOTAC')) return 'ZOTAC';
      if (bUpper.includes('INTEL')) return 'Intel';
      if (bUpper.includes('AMD') || bUpper.includes('RYZEN')) return 'AMD';
      if (bUpper.includes('G.SKILL') || bUpper.includes('GSKILL')) return 'G.Skill';
      return b || 'Other';
    }

    if (key === 'side_panel') {
      if (val) return val;
      if (titleUpper.includes('GLASS') || titleUpper.includes('PANORAMIC') || titleUpper.includes('ARGB') || titleUpper.includes('AIRFLOW') || titleUpper.includes('RGB')) {
        return 'Tempered Glass';
      }
      return 'Tempered Glass';
    }

    if (key === 'psu_rating') {
      if (val) {
        if (val.includes('Gold')) return '80+ Gold';
        if (val.includes('Bronze')) return '80+ Bronze';
        if (val.includes('Platinum')) return '80+ Platinum';
        if (val.includes('White')) return '80+ White';
        return val;
      }
      if (titleUpper.includes('PLATINUM')) return '80+ Platinum';
      if (titleUpper.includes('GOLD')) return '80+ Gold';
      if (titleUpper.includes('BRONZE')) return '80+ Bronze';
      if (titleUpper.includes('WHITE') || titleUpper.includes('80 PLUS')) return '80+ White';
      return '80+ Bronze';
    }

    if (key === 'modular_type') {
      if (val) return val;
      if (titleUpper.includes('FULLY MODULAR') || titleUpper.includes('FULL MODULAR') || titleUpper.includes('MAG A850GL') || titleUpper.includes('MAG A750GL')) {
        return 'Full Modular';
      }
      if (titleUpper.includes('SEMI MODULAR') || titleUpper.includes('SEMI-MODULAR')) return 'Semi Modular';
      return 'Non Modular';
    }

    if (key === 'kit_type') {
      if (val) return val;
      if (titleUpper.includes('2X') || titleUpper.includes('X2') || titleUpper.includes('DUAL') || titleUpper.includes('*2') || titleUpper.includes('2*')) {
        return 'Dual Kit';
      }
      return 'Single';
    }

    if (key === 'capacity_gb') {
      if (val) return val.replace(/[^0-9]/g, '');
      const tbMatch = title.match(/(\d+)\s*TB/i);
      if (tbMatch) return String(parseInt(tbMatch[1]) * 1000);
      const gbMatch = title.match(/(\d+)\s*GB/i);
      if (gbMatch) return gbMatch[1];
      return null;
    }

    if (key === 'interface') {
      if (val) return val;
      if (titleUpper.includes('HDD') || titleUpper.includes('HARD DRIVE') || titleUpper.includes('RPM')) {
        return 'HDD';
      }
      if (titleUpper.includes('NVME') || titleUpper.includes('M.2') || titleUpper.includes('PCIE') || titleUpper.includes('GEN4') || titleUpper.includes('GEN3') || titleUpper.includes('SN7100') || titleUpper.includes('SN850') || titleUpper.includes('SN8100') || titleUpper.includes('SN770') || titleUpper.includes('SN5100') || titleUpper.includes('SN350') || titleUpper.includes('GAMMIX')) {
        return 'NVMe';
      }
      if (titleUpper.includes('SATA') || titleUpper.includes('2.5')) return 'SATA';
      return 'NVMe';
    }

    if (key === 'ram_type') {
      if (val) {
        if (val.toUpperCase().includes('DDR5')) return 'DDR5';
        if (val.toUpperCase().includes('DDR4')) return 'DDR4';
        return val;
      }
      if (titleUpper.includes('DDR5')) return 'DDR5';
      if (titleUpper.includes('DDR4')) return 'DDR4';
      return 'DDR5';
    }

    if (key === 'socket_type') {
      if (val) return val.toUpperCase().replace(/\s+/g, '');
      if (titleUpper.includes('LGA1700') || titleUpper.includes('LGA 1700') || titleUpper.includes('12TH') || titleUpper.includes('13TH') || titleUpper.includes('14TH')) return 'LGA1700';
      if (titleUpper.includes('AM5') || titleUpper.includes('AM 5') || titleUpper.includes('7000') || titleUpper.includes('9000')) return 'AM5';
      if (titleUpper.includes('AM4') || titleUpper.includes('AM 4') || titleUpper.includes('5000')) return 'AM4';
      if (titleUpper.includes('LGA1200') || titleUpper.includes('LGA 1200') || titleUpper.includes('10TH') || titleUpper.includes('11TH')) return 'LGA1200';
      return 'LGA1700';
    }

    if (key === 'form_factor') {
      if (val) {
        const ff = val.toUpperCase().replace(/\s+/g, '');
        if (ff.includes('E-ATX') || ff.includes('EATX')) return 'E-ATX';
        if (ff.includes('MICRO') || ff.includes('MATX') || ff.includes('M-ATX')) return 'mATX';
        if (ff.includes('MINI') || ff.includes('ITX')) return 'ITX';
        if (ff.includes('ATX')) return 'ATX';
        return val;
      }
      if (titleUpper.includes('M-ATX') || titleUpper.includes('MATX') || titleUpper.includes('MICRO-ATX') || titleUpper.includes('M_ATX')) return 'mATX';
      if (titleUpper.includes('ITX')) return 'ITX';
      return 'ATX';
    }

    if (key === 'cooler_type') {
      const str = (val || title).toUpperCase();
      if (str.includes('LIQUID') || str.includes('AIO')) return 'AIO Liquid';
      return 'Air Cooler';
    }

    if (key === 'height_mm') {
      if (val) return val.replace(/[^0-9]/g, '');
      const cType = getMeta(product, 'cooler_type');
      if (cType === 'AIO Liquid') return '55';
      const match = title.match(/(\d{2,3})\s*mm/i);
      if (match && parseInt(match[1]) > 100) return match[1];
      return '155';
    }

    if (key === 'socket_compatibility') {
      let sockets = (val || '').toUpperCase().replace(/\s+/g, '').split(',').filter(Boolean);
      const knownSockets = ['LGA1700', 'LGA1851', 'LGA1200', 'LGA1151', 'LGA1150', 'LGA1155', 'LGA2066', 'AM5', 'AM4'];
      const hasValid = sockets.some(s => knownSockets.includes(s));
      if (!hasValid || sockets.length === 0) {
        return 'LGA1851,LGA1700,LGA1200,LGA1151,AM5,AM4';
      }
      return sockets.join(',');
    }

    if (key === 'supported_form_factors') {
      if (val) return val.replace(/,\s+/g, ',');
      return 'ATX,mATX,ITX';
    }

    if (key === 'radiator_support' || key === 'max_radiator_mm') {
      if (val) return val.replace(/,\s+/g, ',');
      
      // Exact official specifications per model:
      // MSI Mag Forge 120A / 130A: Front 120/140/240mm, Top 120/240mm, Rear 120mm
      if (titleUpper.includes('120A') || titleUpper.includes('130A')) {
        return '120,140,240';
      }
      // MSI SHIELD M500A / PRO FORGE M050A: Top 120/240mm, Rear 120mm
      if (titleUpper.includes('M500A') || titleUpper.includes('M050A')) {
        return '120,240';
      }
      // MSI MAG PANO 100R: Top 120/240/360mm, Side 120/240/360mm, Bottom 120/240/360mm, Rear 120mm
      if (titleUpper.includes('PANO 100R')) {
        return '120,240,360';
      }
      // MSI MAG PANO M100R: Top 120/240/360mm, Rear 120/140mm
      if (titleUpper.includes('PANO M100R')) {
        return '120,140,240,360';
      }
      // Deepcool CL6600: Top pre-installed 360mm / supports 360mm, Rear 120mm
      if (titleUpper.includes('CL6600')) {
        return '120,240,360';
      }
      // Corsair 3500X / 4500X / 4000D / MSI 320R / 321R / Velox 300R / Deepcool CC360 / CG580 / CG530: Supports 120, 140, 240, 280, 360mm
      if (titleUpper.includes('3500X') || titleUpper.includes('4500X') || titleUpper.includes('4000D') || 
          titleUpper.includes('320R') || titleUpper.includes('321R') || titleUpper.includes('VELOX 300R') || 
          titleUpper.includes('CC360') || titleUpper.includes('CG580') || titleUpper.includes('CG530')) {
        return '120,140,240,280,360';
      }

      return '120,240,360';
    }

    if (key === 'vram_gb') {
      if (val) return val.replace(/[^0-9]/g, '');
      const vMatch = title.match(/(\d+)\s*GB/i);
      if (vMatch) return vMatch[1];
      return '8';
    }

    if (key === 'series') {
      if (val) return val;
      const sMatch = title.match(/(RTX\s*\d{4}(?:\s*Ti|\s*SUPER)?|RX\s*\d{4}(?:\s*XT)?)/i);
      if (sMatch) return sMatch[1].replace(/\s+/g, ' ').toUpperCase();
      return 'Graphics Card';
    }

    if (key === 'tdp_watts') {
      if (val) return val.replace(/[^0-9]/g, '');
      if (titleUpper.includes('5090') || titleUpper.includes('4090')) return '450';
      if (titleUpper.includes('5080') || titleUpper.includes('4080')) return '320';
      if (titleUpper.includes('5070') || titleUpper.includes('4070')) return '220';
      if (titleUpper.includes('5060') || titleUpper.includes('4060')) return '160';
      return '180';
    }

    if (key === 'length_mm') {
      if (val) return val.replace(/[^0-9]/g, '');
      return '300';
    }

    if (key === 'wattage') {
      if (val) return val.replace(/[^0-9]/g, '');
      const wMatch = title.match(/(\d{3,4})\s*(?:W|Watt)/i);
      if (wMatch) return wMatch[1];
      const numMatch = title.match(/(?:A|RM|CX|TX|CV|MAG)?\s*(\d{3,4})/i);
      if (numMatch && parseInt(numMatch[1]) >= 400 && parseInt(numMatch[1]) <= 1600) return numMatch[1];
      return '750';
    }

    return val;
  }

  function getPrice(product) {
    if (!product || !product.variants || !product.variants[0]) return 0;
    return parseFloat(product.variants[0].price) || 0;
  }

  function formatPrice(price) {
    return price.toLocaleString('en-IN');
  }

  function escHtml(str) {
    return String(str).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  let renderTimer;
  function debouncedRender(stepIndex) {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(() => renderProducts(stepIndex), 200);
  }

  /* ============================================================
     START
     ============================================================ */
  function safeInit() {
    // Only run if the builder section is present on this page
    if (!$('bpcGrid') || !$('bpcStepsTabs')) return;
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeInit);
  } else {
    safeInit();
  }

})();
